/**
 * CCD (Cyclic Coordinate Descent) IK solver for general chains.
 *
 * Used for spine, neck, and other multi-bone chains where analytic
 * 2-bone IK doesn't apply. Iteratively rotates each joint toward
 * the target, converging over multiple iterations.
 */

import { Matrix4, Quaternion, Vector3 } from 'three';
import type { Skeleton } from '../skeleton/core';

const _effectorPos = new Vector3();
const _targetDir = new Vector3();
const _effectorDir = new Vector3();
const _axis = new Vector3();
const _quat = new Quaternion();

export type CCDIKChain = {
  boneIndices: number[];
  effectorIndex: number;
};

export type CCDIKOptions = {
  iterations?: number;
  tolerance?: number;
  maxAnglePerStep?: number;
};

/**
 * Solve IK for a chain using CCD algorithm.
 * Modifies `skeleton.bones[i].poseLocal` for bones in the chain.
 *
 * @param skeleton     The skeleton to modify
 * @param chain        Chain definition (ordered bone indices, root to tip)
 * @param targetPos    Desired world position for the effector
 * @param options      Solver options
 * @returns true if converged within tolerance
 */
export function solveCCDIK(
  skeleton: Skeleton,
  chain: CCDIKChain,
  targetPos: Vector3,
  options?: CCDIKOptions,
): boolean {
  const { iterations = 10, tolerance = 0.001, maxAnglePerStep = Math.PI * 0.5 } =
    options ?? {};

  const { boneIndices, effectorIndex } = chain;

  for (let iter = 0; iter < iterations; iter++) {
    skeleton.computePosedWorldTransforms();

    _effectorPos.setFromMatrixPosition(skeleton.bone(effectorIndex).posedWorld);
    const dist = _effectorPos.distanceTo(targetPos);
    if (dist < tolerance) return true;

    for (let i = boneIndices.length - 1; i >= 0; i--) {
      const boneIdx = boneIndices[i];
      const bone = skeleton.bone(boneIdx);

      skeleton.computePosedWorldTransforms();
      _effectorPos.setFromMatrixPosition(skeleton.bone(effectorIndex).posedWorld);

      const jointPos = new Vector3().setFromMatrixPosition(bone.posedWorld);

      _effectorDir.subVectors(_effectorPos, jointPos);
      _targetDir.subVectors(targetPos, jointPos);

      if (_effectorDir.lengthSq() < 1e-10 || _targetDir.lengthSq() < 1e-10) {
        continue;
      }

      _effectorDir.normalize();
      _targetDir.normalize();

      const dot = Math.max(-1, Math.min(1, _effectorDir.dot(_targetDir)));
      let angle = Math.acos(dot);
      if (angle < 1e-6) continue;

      angle = Math.min(angle, maxAnglePerStep);

      _axis.crossVectors(_effectorDir, _targetDir).normalize();
      if (_axis.lengthSq() < 1e-10) continue;

      _quat.setFromAxisAngle(_axis, angle);

      const worldRotDelta = new Matrix4().makeRotationFromQuaternion(_quat);

      const parentPosedWorld =
        bone.parent !== null
          ? skeleton.bone(bone.parent).posedWorld
          : skeleton.rootParentWorld;
      const parentPosedWorldInv = new Matrix4().copy(parentPosedWorld).invert();

      const currentLocal = new Matrix4().multiplyMatrices(bone.restLocal, bone.poseLocal);
      const currentWorld = new Matrix4().multiplyMatrices(parentPosedWorld, currentLocal);

      const newWorld = new Matrix4().copy(worldRotDelta).multiply(currentWorld);
      const newLocal = new Matrix4().copy(parentPosedWorldInv).multiply(newWorld);

      const restLocalInv = new Matrix4().copy(bone.restLocal).invert();
      bone.poseLocal.copy(restLocalInv).multiply(newLocal);
    }
  }

  skeleton.computePosedWorldTransforms();
  _effectorPos.setFromMatrixPosition(skeleton.bone(effectorIndex).posedWorld);
  return _effectorPos.distanceTo(targetPos) < tolerance;
}

/**
 * Reset pose_local to identity for all bones in a chain.
 */
export function resetChainPose(skeleton: Skeleton, chain: CCDIKChain): void {
  for (const idx of chain.boneIndices) {
    skeleton.bone(idx).poseLocal.identity();
  }
  skeleton.bone(chain.effectorIndex).poseLocal.identity();
  skeleton.computePosedWorldTransforms();
}
